import { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { Euro, RefreshCw, AlertCircle, Ticket, FileText, Download, Plus, Ban, Undo2, Tag } from "lucide-react";

export default function AdminBilling() {
  const [tab, setTab] = useState("subscriptions");
  return (
    <div className="space-y-6" data-testid="admin-billing-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-400">Lot 2 — Argent</p>
        <h1 className="text-3xl font-semibold text-white flex items-center gap-3"><Euro className="w-7 h-7 text-emerald-400" /> Facturation & Abonnements</h1>
      </div>
      <div className="inline-flex bg-[#0f172a] border border-slate-800 rounded-md p-1" data-testid="billing-tabs">
        {[
          ["subscriptions", "Abonnements", RefreshCw],
          ["reconciliation", "Réconciliation", AlertCircle],
          ["dunning", "Dunning", Ban],
          ["coupons", "Coupons", Ticket],
          ["invoices", "Factures", FileText],
          ["accounting", "Export compta", Download],
          ["reprice", "Tarifs Stripe", Tag],
        ].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs ${tab === k ? "bg-[#1F5A99] text-white" : "text-slate-400 hover:text-white"}`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>
      {tab === "subscriptions" && <Subscriptions />}
      {tab === "reconciliation" && <Reconciliation />}
      {tab === "dunning" && <Dunning />}
      {tab === "coupons" && <Coupons />}
      {tab === "invoices" && <Invoices />}
      {tab === "accounting" && <Accounting />}
      {tab === "reprice" && <Reprice />}
    </div>
  );
}

function Subscriptions() {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(false);
  useEffect(() => { (async () => { setLoading(true); try { const {data}=await adminApi.get("/subscriptions"); setRows(data.items || []); } catch { toast.error("Erreur"); } finally { setLoading(false); } })(); }, []);
  return (
    <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-4">
      {loading ? <p className="text-slate-500 text-sm">Chargement Stripe…</p> : (
        <table className="w-full text-xs">
          <thead><tr className="uppercase tracking-widest text-[10px] text-slate-500 text-left">
            <th className="p-2">Sub ID</th><th className="p-2">E-mail</th><th className="p-2">Statut</th><th className="p-2">MRR</th><th className="p-2">Fin période</th><th className="p-2">Créé</th>
          </tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.id} className="border-t border-slate-800" data-testid={`sub-row-${r.id.slice(0,8)}`}>
              <td className="p-2 font-mono text-slate-500">{r.id.slice(0,20)}…</td>
              <td className="p-2">{r.email}</td>
              <td className="p-2"><span className={`px-2 py-0.5 rounded text-[10px] uppercase font-mono ${r.status === "active" ? "bg-emerald-500/20 text-emerald-300" : r.status === "past_due" ? "bg-rose-500/20 text-rose-300" : "bg-slate-800 text-slate-400"}`}>{r.status}</span></td>
              <td className="p-2 font-mono text-emerald-400">{r.mrr_eur} €</td>
              <td className="p-2 font-mono text-slate-500">{r.current_period_end?.slice(0,10)}</td>
              <td className="p-2 font-mono text-slate-500">{r.created?.slice(0,10)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">Aucun abonnement</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Reconciliation() {
  const [r, setR] = useState(null); const [running, setRunning] = useState(false);
  const load = async () => { try { const {data}=await adminApi.get("/reconciliation"); setR(data);} catch (e) { console.error("Reconciliation load failed:", e); } };
  useEffect(() => { load(); }, []);
  const run = async () => { setRunning(true); try { const {data}=await adminApi.post("/reconciliation/run"); setR(data); toast.success("Réconciliation lancée");} catch { toast.error("Erreur"); } finally { setRunning(false); } };
  return (
    <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6">
      <div className="flex justify-between mb-4">
        <p className="text-xs text-slate-400">Compare Stripe charges vs orders locales (30 derniers jours).</p>
        <button onClick={run} disabled={running} data-testid="reconciliation-run"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-xs">
          <RefreshCw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} /> Lancer maintenant
        </button>
      </div>
      {r && !r.never_run && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card l="Charges Stripe (30j)" v={r.stripe_charges_count} />
          <Card l="Orders locales (30j)" v={r.local_orders_count} />
          <Card l="Écarts" v={r.has_discrepancies ? "⚠" : "✓"} tone={r.has_discrepancies ? "amber" : "emerald"} />
        </div>
      )}
      {r?.has_discrepancies && (
        <div className="mt-4 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs">
          <p><b>Orphelins locaux:</b> {r.orphan_local?.length || 0} — <b>Orphelins Stripe:</b> {r.orphan_stripe?.length || 0}</p>
        </div>
      )}
      {r?.never_run && <p className="text-slate-500 text-sm">Jamais exécutée.</p>}
    </div>
  );
}

function Dunning() {
  const [rows, setRows] = useState([]);
  const load = async () => { try { const {data}=await adminApi.get("/dunning/log"); setRows(data.items || []);} catch (e) { console.error("Dunning load failed:", e); } };
  useEffect(() => { load(); }, []);
  const run = async () => { try { const {data}=await adminApi.post("/dunning/run"); toast.success(`${data.count} suspendus`); load(); } catch { toast.error("Erreur"); } };
  return (
    <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-4">
      <div className="flex justify-between mb-4">
        <p className="text-xs text-slate-400">Suspension automatique à J+15 d'impayé.</p>
        <button onClick={run} data-testid="dunning-run" className="px-4 py-2 rounded-md bg-emerald-600 text-white text-xs">Lancer</button>
      </div>
      <table className="w-full text-xs">
        <thead><tr className="uppercase text-[10px] text-slate-500 text-left"><th className="p-2">Date</th><th className="p-2">E-mail</th><th className="p-2">Jours</th><th className="p-2">Action</th></tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.id} className="border-t border-slate-800">
            <td className="p-2 font-mono">{r.created_at?.slice(0,16).replace("T"," ")}</td>
            <td className="p-2">{r.email}</td>
            <td className="p-2 font-mono text-amber-400">J+{r.days_past_due}</td>
            <td className="p-2">{r.action}</td>
          </tr>
        ))}{rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500">Aucune action de dunning</td></tr>}</tbody>
      </table>
    </div>
  );
}

function Coupons() {
  const [rows, setRows] = useState([]);
  const [percent, setPercent] = useState(20);
  const [duration, setDuration] = useState("once");
  const [maxRedemptions, setMaxRedemptions] = useState(50);
  const [expiresDays, setExpiresDays] = useState(30);
  const load = async () => { try { const {data} = await adminApi.get("/coupons"); setRows(data);} catch (e) { console.error("Coupons load failed:", e); } };
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await adminApi.post("/coupons", { percent_off: percent, duration, max_redemptions: maxRedemptions, expires_days: expiresDays });
      toast.success("Coupon créé"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const deactivate = async (id) => {
    if (!window.confirm("Désactiver ?")) return;
    try { await adminApi.post(`/coupons/${id}/deactivate`); load(); toast.success("Désactivé"); } catch { toast.error("Erreur"); }
  };
  return (
    <div className="space-y-4">
      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-5">
        <h3 className="text-sm font-semibold mb-3">Créer un coupon</h3>
        <div className="grid md:grid-cols-5 gap-3">
          <select value={percent} onChange={e => setPercent(parseInt(e.target.value))} data-testid="coupon-percent"
            className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs">
            {[10, 20, 50, 100].map(p => <option key={p} value={p}>{p}% de réduction</option>)}
          </select>
          <select value={duration} onChange={e => setDuration(e.target.value)} data-testid="coupon-duration"
            className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs">
            <option value="once">Une seule fois</option>
            <option value="forever">Récurrent (forever)</option>
          </select>
          <input type="number" value={maxRedemptions} onChange={e => setMaxRedemptions(parseInt(e.target.value))}
            placeholder="Max utilisations" data-testid="coupon-max"
            className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
          <input type="number" value={expiresDays} onChange={e => setExpiresDays(parseInt(e.target.value))}
            placeholder="Expire (jours)" data-testid="coupon-expires"
            className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
          <button onClick={create} data-testid="coupon-create-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-xs"><Plus className="w-3.5 h-3.5" /> Créer</button>
        </div>
      </div>
      <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-4">
        <table className="w-full text-xs">
          <thead><tr className="uppercase text-[10px] text-slate-500 text-left"><th className="p-2">Code</th><th className="p-2">%</th><th className="p-2">Durée</th><th className="p-2">Max</th><th className="p-2">Utilisés</th><th className="p-2">Actif</th><th className="p-2"></th></tr></thead>
          <tbody>{rows.map(c => (
            <tr key={c.id} className="border-t border-slate-800" data-testid={`coupon-${c.code}`}>
              <td className="p-2 font-mono text-emerald-400">{c.code}</td>
              <td className="p-2 font-mono">{c.percent_off}%</td>
              <td className="p-2">{c.duration}</td>
              <td className="p-2 font-mono">{c.max_redemptions || "—"}</td>
              <td className="p-2 font-mono">{c.times_redeemed ?? "?"}</td>
              <td className="p-2">{c.active ? "✓" : "✗"}</td>
              <td className="p-2">{c.active && <button onClick={() => deactivate(c.id)} className="text-rose-400 hover:underline"><Ban className="w-3 h-3" /></button>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">Aucun coupon</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Invoices() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [q, setQ] = useState(""); const [month, setMonth] = useState("");
  const [page, setPage] = useState(1);
  const load = async () => {
    const p = new URLSearchParams({ page: String(page), per_page: "25" });
    if (q) p.set("q", q); if (month) p.set("month", month);
    try { const {data} = await adminApi.get(`/invoices?${p}`); setData(data); } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);
  const [creditReason, setCreditReason] = useState({});
  const doCredit = async (id) => {
    const reason = creditReason[id] || prompt("Motif de l'avoir ?");
    if (!reason || reason.length < 3) return;
    try { await adminApi.post(`/invoices/${id}/credit-note`, { reason }); toast.success("Avoir créé"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const doRefund = async (o) => {
    const reason = prompt("Motif du remboursement ?");
    if (!reason || reason.length < 3) return;
    const conf = prompt(`Confirmez en retapant le n° de facture "${o.invoice_number}"`);
    if (conf !== o.invoice_number) return toast.error("Confirmation incorrecte");
    try { await adminApi.post(`/invoices/${o.id}/refund`, { reason, confirm_invoice_number: conf }); toast.success("Remboursement effectué"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-4">
      <div className="flex flex-wrap gap-3 mb-4">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="N° facture / label" data-testid="invoices-search" className="flex-1 min-w-[200px] bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="invoices-month" className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
        <button onClick={() => (setPage(1), load())} className="px-4 py-2 rounded-md bg-[#1F5A99] text-white text-xs">Filtrer</button>
      </div>
      <table className="w-full text-xs">
        <thead><tr className="uppercase text-[10px] text-slate-500 text-left"><th className="p-2">N°</th><th className="p-2">Date</th><th className="p-2">Client</th><th className="p-2">Label</th><th className="p-2">Total TTC</th><th className="p-2">Statut</th><th className="p-2">Actions</th></tr></thead>
        <tbody>{data.items.map(o => (
          <tr key={o.id} className="border-t border-slate-800" data-testid={`invoice-${o.invoice_number}`}>
            <td className="p-2 font-mono">{o.invoice_number}</td>
            <td className="p-2 font-mono text-slate-500">{o.created_at?.slice(0,10)}</td>
            <td className="p-2 text-slate-300">{o.client?.email || "—"}</td>
            <td className="p-2 max-w-xs truncate">{o.label}</td>
            <td className="p-2 font-mono text-emerald-400">{o.total_eur} €</td>
            <td className="p-2"><span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded ${o.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : o.status === "refunded" ? "bg-rose-500/20 text-rose-300" : "bg-slate-800 text-slate-400"}`}>{o.status}</span></td>
            <td className="p-2 flex gap-2">
              {o.status === "paid" && !o.credited && (
                <>
                  <button onClick={() => doCredit(o.id)} data-testid={`credit-${o.invoice_number}`} className="text-amber-400 hover:underline text-[10px]">Avoir</button>
                  <button onClick={() => doRefund(o)} data-testid={`refund-${o.invoice_number}`} className="text-rose-400 hover:underline text-[10px]">Remb.</button>
                </>
              )}
              {o.credited && <span className="text-slate-500 text-[10px]">Crédité</span>}
            </td>
          </tr>
        ))}</tbody>
      </table>
      <div className="flex justify-between mt-3 text-xs">
        <span className="text-slate-500">{data.total} facture(s)</span>
        <div className="flex gap-2">
          <button disabled={page === 1} onClick={() => setPage(p => p-1)} className="px-3 py-1.5 rounded-md border border-slate-800 disabled:opacity-40">Préc.</button>
          <button onClick={() => setPage(p => p+1)} className="px-3 py-1.5 rounded-md border border-slate-800">Suiv.</button>
        </div>
      </div>
    </div>
  );
}

function Accounting() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0,7));
  const dl = async () => {
    const token = localStorage.getItem("empco_admin_token");
    const r = await fetch(`${adminApi.defaults.baseURL}/accounting/export?month=${month}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return toast.error("Erreur");
    const blob = await r.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `empco-comptable-${month}.zip`; a.click();
    toast.success("Export téléchargé");
  };
  return (
    <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6">
      <h3 className="text-sm font-semibold mb-4">Export comptable mensuel</h3>
      <p className="text-xs text-slate-400 mb-4">ZIP contenant le CSV récapitulatif + tous les PDF factures du mois.</p>
      <div className="flex gap-3 items-center">
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="accounting-month"
          className="bg-[#0b1220] border border-slate-700 rounded-md px-3 py-2 text-xs" />
        <button onClick={dl} data-testid="accounting-dl"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-xs"><Download className="w-3.5 h-3.5" /> Télécharger ZIP</button>
      </div>
    </div>
  );
}

function Card({ l, v, tone = "slate" }) {
  const tones = { slate: "text-white", emerald: "text-emerald-400", amber: "text-amber-400" };
  return <div className="bg-[#0b1220] rounded-md p-4"><p className="text-[10px] uppercase text-slate-500">{l}</p><p className={`text-2xl font-semibold ${tones[tone]}`}>{v}</p></div>;
}

function Reprice() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (dry_run) => {
    if (!dry_run && !window.confirm("Confirmer le changement de tarifs Stripe en LIVE ?\n\nCela va archiver les anciens Prices et créer les nouveaux. Les abonnements existants continueront sur l'ancien Price jusqu'à leur prochain cycle.")) return;
    setRunning(true);
    try {
      const { data } = await adminApi.post("/stripe/reprice", { dry_run });
      setResult(data);
      toast.success(dry_run ? "Dry-run OK — aucun changement" : "Nouveaux prix Stripe créés ✅");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur reprice");
    } finally { setRunning(false); }
  };

  return (
    <div className="bg-[#0f172a] border border-slate-800 rounded-lg p-6 space-y-5" data-testid="reprice-panel">
      <div>
        <h2 className="text-lg text-white font-semibold flex items-center gap-2"><Tag className="w-5 h-5 text-emerald-400" /> Repricing Stripe (LIVE)</h2>
        <p className="text-xs text-slate-400 mt-1">Applique les tarifs actuels du site (20 € scan, 200 € /mois surveillance). Idempotent — si déjà bon, ne fait rien.</p>
      </div>
      <div className="flex gap-3">
        <button onClick={() => run(true)} disabled={running} data-testid="reprice-dry-btn"
          className="px-4 py-2 rounded bg-slate-800 text-slate-200 text-sm hover:bg-slate-700 disabled:opacity-50">
          {running ? "…" : "Simuler (dry-run)"}
        </button>
        <button onClick={() => run(false)} disabled={running} data-testid="reprice-live-btn"
          className="px-4 py-2 rounded bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
          {running ? "…" : "Exécuter en LIVE"}
        </button>
      </div>
      {result && (
        <div className="mt-4 space-y-3" data-testid="reprice-result">
          <p className="text-xs uppercase text-slate-500">Résultat {result.dry_run ? "(dry-run)" : "(LIVE)"}</p>
          {result.items.map((it) => (
            <div key={it.lookup_key} className="bg-[#0b1220] rounded p-4 text-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-emerald-400">{it.lookup_key}</span>
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${
                  it.action === "replaced" ? "bg-emerald-900/50 text-emerald-300" :
                  it.action === "unchanged" ? "bg-slate-800 text-slate-400" :
                  "bg-amber-900/50 text-amber-300"}`}>{it.action}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-300">
                <div>Ancien : <span className="font-mono">{it.old_price_id || "—"}</span></div>
                <div>Nouveau : <span className="font-mono text-emerald-400">{it.new_price_id}</span></div>
                {it.old_amount != null && <div>Ancien montant : {(it.old_amount / 100).toFixed(2)} €</div>}
                {it.new_amount != null && <div>Nouveau montant : {(it.new_amount / 100).toFixed(2)} €</div>}
              </div>
            </div>
          ))}
          <p className="text-[11px] text-amber-300 italic">{result.note}</p>
        </div>
      )}
    </div>
  );
}
